import React from "react";
import { useTranslation } from "react-i18next";
import LegalPageShell from "../../components/LegalPageShell";

export default function TermsOfService() {
  const { t } = useTranslation(["legalTerms", "common", "legal"]);

  const sections = [
    { id: "introduction", label: t("introduction", "Introduction") },
    { id: "acceptance", label: t("acceptanceOfTerms", "Acceptance of Terms") },
    { id: "account-use", label: t("accountUse", "Account Use") },
    { id: "platform-features", label: t("platformFeaturesAndScope", "Platform Features and Scope") },
    { id: "content-conduct", label: t("contentAndConductRules", "Content and Conduct Rules") },
    { id: "user-responsibility", label: t("userResponsibility", "User Responsibility") },
    { id: "payments", label: t("paymentsAndFinancialTerms", "Payments and Financial Terms") },
    { id: "moderation", label: t("moderationAndEnforcement", "Moderation and Enforcement") },
    { id: "disclaimer", label: t("disclaimerAndLimitation", "Disclaimer and Limitation") },
    { id: "policy-relationship", label: t("policyRelationship", "Policy Relationship") },
  ];

  return (
<LegalPageShell
        title={t("title", "Terms of Service")}
        subtitle={t("subtitle", "Terms governing use of Orrith and its Cycle of Certainty web platform.")}
        effectiveDate={`Effective: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`}
        sections={sections}
        namespace="legalTerms"
      >
      <section id="introduction" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("introduction", "Introduction")}
        </h2>
        <p className="legal-prose">
          {t("termsIntro", "Welcome to Orrith. These Terms of Service (\"Terms\") govern your access to and use of the Orrith platform at orrith.me, including all related web experiences, features, and services. By accessing or using Orrith, you agree to be bound by these Terms. If you do not agree to these Terms, do not use the platform.")}
        </p>
        <p className="legal-prose mt-4">
          {t("termsScope", "These Terms work alongside our Privacy Policy, Security Policy, Community Guidelines, Cookie Policy, Accessibility Statement, and Refund Policy. Where a feature has additional rules, those feature-specific rules apply in addition to these Terms. We may update these Terms from time to time; the effective date and version at the top of this page indicate the currently active version.")}
        </p>
      </section>

      <section id="acceptance" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("acceptanceOfTerms", "Acceptance of Terms")}
        </h2>
        <p className="legal-prose">
          {t("acceptanceDesc", "By accessing or using Orrith, you agree to these Terms of Service. If you do not agree, do not use the platform. You represent that you are at least 13 years old and have the legal capacity to enter into these Terms. If you are using Orrith on behalf of an organization, you represent that you have authority to bind that organization to these Terms.")}
        </p>
      </section>

      <section id="account-use" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("accountUse", "Account Use")}
        </h2>
        <ul className="legal-list">
          <li>{t("accountUse.responsible", "You are responsible for all activity performed under your account")}</li>
          <li>{t("accountUse.accurateInfo", "You must provide accurate account information and keep it reasonably current")}</li>
          <li>{t("accountUse.authRequired", "Some features require authentication; some sensitive features also require verified email")}</li>
          <li>{t("accountUse.restrictions", "Accounts may be restricted or suspended for policy violations or abuse")}</li>
          <li>{t("accountUse.credentials", "You are responsible for safeguarding your credentials and for all activities that occur under your account")}</li>
        </ul>
      </section>

      <section id="platform-features" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("platformFeaturesAndScope", "Platform Features and Scope")}
        </h2>
        <p className="legal-prose mb-6">
          {t("featuresIntro", "Orrith provides an integrated agriculture intelligence platform with the following core capabilities:")}
        </p>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("coreCapabilities", "Core Capabilities")}
        </h3>
        <ul className="legal-list">
          <li>{t("coreCapabilitiesItems.listing", "Product listing and discovery")}</li>
          <li>{t("coreCapabilitiesItems.offers", "Offer workflows and status updates")}</li>
          <li>{t("coreCapabilitiesItems.messaging", "User messaging and offer workflows between participants")}</li>
          <li>{t("coreCapabilitiesItems.farmPulse", "FarmPulse market contribution and insights views")}</li>
          <li>{t("coreCapabilitiesItems.farmMark", "FarmMark impact views and leaderboard experiences")}</li>
        </ul>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("operationalFeatures", "Operational Features")}
        </h3>
        <ul className="legal-list">
          <li>{t("operationalFeaturesItems.profile", "Profile visibility and privacy controls")}</li>
          <li>{t("operationalFeaturesItems.support", "Support tickets, feedback submissions, and error reporting")}</li>
          <li>{t("operationalFeaturesItems.moderation", "Administrative moderation and policy enforcement tools")}</li>
          <li>{t("operationalFeaturesItems.dataExport", "Data export and account deletion requests through Settings")}</li>
        </ul>

        <p className="legal-prose mt-6">
          {t("featuresChanges", "Orrith may update, modify, pause, or discontinue platform features at any time to maintain reliability, improve security, or comply with legal and operational requirements. We will provide reasonable notice for material changes where feasible.")}
        </p>
      </section>

      <section id="content-conduct" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("contentAndConductRules", "Content and Conduct Rules")}
        </h2>
        <p className="legal-prose mb-6">
          {t("conductIntro", "All users must comply with the following rules when using Orrith:")}
        </p>
        <ul className="legal-list">
          <li>{t("contentRules.falseContent", "Do not post false, deceptive, abusive, or unlawful content")}</li>
          <li>{t("contentRules.misrepresent", "Do not misrepresent listing details or identity")}</li>
          <li>{t("contentRules.unauthorized", "Do not attempt unauthorized access, data scraping, or platform disruption")}</li>
          <li>{t("contentRules.harassment", "Do not use the platform to harass, threaten, or discriminate against others")}</li>
          <li>{t("contentRules.comply", "Comply with moderator instructions and enforcement outcomes")}</li>
        </ul>
      </section>

      <section id="user-responsibility" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("userResponsibility", "User Responsibility for Listings and Messages")}
        </h2>
        <p className="legal-prose mb-6">
          {t("responsibilityIntro", "You are solely responsible for your activity on Orrith:")}
        </p>
        <ul className="legal-list">
          <li>{t("userResp.productDetails", "You are responsible for accuracy of your product details")}</li>
          <li>{t("userResp.lawfulHandling", "You are responsible for lawful handling, transfer, and local compliance of goods")}</li>
          <li>{t("userResp.communications", "You are responsible for communications sent from your account")}</li>
          <li>{t("userResp.locationData", "You are responsible for location data you choose to share")}</li>
        </ul>
      </section>

      <section id="payments" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("paymentsAndFinancialTerms", "Payments and Financial Terms")}
        </h2>
        <p className="legal-prose mb-4">
          {t("payments.currentState", "Current in-app payment components are demo/testing flows and not a live payment settlement system. Orrith does not currently process live in-app checkout settlements through the web payment modal.")}
        </p>
        <p className="legal-prose">
          {t("payments.futureTerms", "Any future live payment processing, fees, and settlement terms will be published in an updated version of these Terms and related policies. Refund eligibility, timelines, and processing rules will be clearly communicated when production payment processing is enabled.")}
        </p>
      </section>

      <section id="moderation" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("moderationAndEnforcement", "Moderation and Enforcement")}
        </h2>
        <p className="legal-prose mb-6">
          {t("moderationIntro", "Orrith reserves the right to moderate content and enforce these Terms:")}
        </p>
        <ul className="legal-list">
          <li>{t("moderation.contentFlagged", "Content may be flagged, hidden, removed, or restricted through moderation workflows")}</li>
          <li>{t("moderation.accountsWarned", "Accounts may be warned, suspended, or banned for severe or repeated violations")}</li>
          <li>{t("moderation.supportReviewed", "Support and trust reports may be reviewed by authorized administrative users")}</li>
          <li>{t("moderation.adminLogged", "Administrative actions may be logged for audit and service integrity")}</li>
        </ul>
      </section>

      <section id="disclaimer" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("disclaimerAndLimitation", "Disclaimer and Limitation of Liability")}
        </h2>
        <p className="legal-prose mb-4">
          {t("disclaimer.desc", "Orrith provides software infrastructure for coordination and visibility. Except where prohibited by law, the service is provided on an \"as is\" and \"as available\" basis, without warranties of any kind, either express or implied.")}
        </p>
        <p className="legal-prose">
          {t("disclaimer.liability", "Users remain responsible for decisions, transactions, and outcomes arising from their own use of the platform. Orrith shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly.")}
        </p>
      </section>

      <section id="policy-relationship" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("policyRelationship", "Policy Relationship")}
        </h2>
        <p className="legal-prose">
          {t("policyRel.desc", "These Terms work alongside the Privacy Policy, Security Policy, Community Guidelines, Cookie Policy, Accessibility Statement, and Refund Policy. Where a feature has additional rules, those feature-specific rules apply in addition to these Terms. In case of conflict between these Terms and a feature-specific policy, the feature-specific policy governs for that feature.")}
        </p>
      </section>
    </LegalPageShell>
  );
}